import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usuariosAPI, evaluacionesAPI, getImageUrl } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useCerrarMenuAlClickFuera } from '../hooks/useCerrarMenuAlClickFuera';
import DetalleEvaluacionModal from './DetalleEvaluacionModal';
import ResultadoPopup from './ResultadoPopup';
import './HistorialEvaluaciones.css';

const HistorialEvaluaciones = () => {
  const { usuario: usuarioAuth, logout, esAdmin } = useAuth();
  const [usuario, setUsuario] = useState(null);
  const [evaluaciones, setEvaluaciones] = useState([]);
  const [menuAbierto, setMenuAbierto] = useState(false);
  useCerrarMenuAlClickFuera(menuAbierto, setMenuAbierto);
  const [mostrarDetalle, setMostrarDetalle] = useState(false);
  const [mostrarResultadoPDF, setMostrarResultadoPDF] = useState(false);
  const [evaluacionSeleccionada, setEvaluacionSeleccionada] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (usuarioAuth?.id) {
      cargarDatos();
    }
  }, [usuarioAuth]);

  const cargarDatos = async () => {
    try {
      const respuesta = await usuariosAPI.obtenerPorId(usuarioAuth.id);
      setUsuario(respuesta.data);
      cargarHistorial(usuarioAuth.id);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    }
  };

  const cargarHistorial = async (usuarioId) => {
    try {
      const respuesta = await usuariosAPI.obtenerHistorial(usuarioId);
      setEvaluaciones(respuesta.data);
    } catch (error) {
      console.error('Error al cargar historial:', error);
    }
  };


  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const volverDashboard = () => {
    navigate('/');
  };

  const iniciarEvaluacion = () => {
    if (usuario) {
      navigate(`/evaluacion/${usuario.id}`);
    }
  };

  const editarEvaluacion = (evaluacionId) => {
    navigate(`/evaluacion/${usuario.id}`);
  };

  const eliminarEvaluacion = async (evaluacionId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta evaluación?')) {
      try {
        await evaluacionesAPI.eliminar(evaluacionId);
        cargarDatos();
      } catch (error) {
        console.error('Error al eliminar evaluación:', error);
      }
    }
  };

  const verEvaluacion = (evaluacion) => {
    setEvaluacionSeleccionada(evaluacion);
    setMostrarDetalle(true);
  };

  const verPDF = (evaluacion) => {
    setEvaluacionSeleccionada(evaluacion);
    setMostrarResultadoPDF(true);
  };

  const getNivelLiderazgo = (puntuacion) => {
    if (puntuacion <= 20) return 'Liderazgo en desarrollo';
    if (puntuacion <= 40) return 'Liderazgo intermedio';
    if (puntuacion <= 50) return 'Liderazgo avanzado';
    if (puntuacion <= 80) return 'Liderazgo experto';
    return 'Liderazgo transformacional';
  };

  const cerrarDetalle = () => {
    setMostrarDetalle(false);
    setEvaluacionSeleccionada(null);
  };

  const cerrarResultadoPDF = () => {
    setMostrarResultadoPDF(false);
    setEvaluacionSeleccionada(null);
  };

  return (
    <>
      <div className="historial-container">
        <button className="menu-toggle" onClick={() => setMenuAbierto(!menuAbierto)}>
          ☰
        </button>

        <aside className={`historial-sidebar ${menuAbierto ? 'abierto' : ''}`}>
          <div className="user-profile">
            <div className="avatar-circle" style={{ backgroundImage: usuario?.imagen ? `url(${getImageUrl(usuario.imagen)})` : 'none' }}>
              {!usuario?.imagen && <span className="avatar-placeholder">📷</span>}
            </div>
            <h2 className="user-name">{usuario?.nombre || 'Usuario'}</h2>
            <div className="user-stats">
              <p>Nivel de liderazgo: {usuario?.nivel_liderazgo || 'Sin evaluar'}</p>
              <p>Puntuación: {usuario?.puntuacion ? Math.round(usuario.puntuacion) : '0'}</p>
            </div>
          </div>

          <div className="sidebar-buttons">
            <button className="btn-sidebar" onClick={iniciarEvaluacion}>
              Iniciar evaluación
            </button>
            <button className="btn-sidebar active">
              Historial de evaluaciones
            </button>
            {esAdmin && (
              <button className="btn-sidebar" onClick={() => navigate('/admin')}>
                Panel admin
              </button>
            )}
          </div>

          <div className="logout-section">
            <div className="logout-text" onClick={() => {
              logout();
              navigate('/login');
            }}>
              Cerrar sesión
            </div>
            <button className="home-btn" onClick={() => navigate('/')} aria-label="Inicio">
              <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '26px', height: '26px' }}>
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
              </svg>
            </button>
          </div>
        </aside>

        <main className="historial-main">
          <div className="historial-header">
            <h1>Historial de Evaluaciones</h1>
            <button className="btn-volver" onClick={volverDashboard}>
              ← Volver al Dashboard
            </button>
          </div>

          {evaluaciones.length === 0 ? (
            <div className="no-evaluaciones">
              <p>No hay evaluaciones previas</p>
              <button className="btn-primary" onClick={iniciarEvaluacion}>
                Iniciar primera evaluación
              </button>
            </div>
          ) : (
            <div className="tabla-container">
              <table className="tabla-evaluaciones">
                <thead>
                  <tr>
                    <th>Evaluación Nro</th>
                    <th>Resultado</th>
                    <th>Fecha</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {evaluaciones.map((evaluacion, index) => (
                    <tr key={evaluacion.id} className={!evaluacion.completada ? 'evaluacion-inconclusa-row' : ''}>
                      <td data-label="Evaluación Nro">
                        #{evaluaciones.length - index}
                        {!evaluacion.completada && <span className="badge-inconclusa-tabla">Inconclusa</span>}
                      </td>
                      <td data-label="Resultado">
                        {evaluacion.completada ? Math.round(evaluacion.puntuacion_total) : 'Sin completar'}
                      </td>
                      <td data-label="Fecha">{formatearFecha(evaluacion.fecha_evaluacion)}</td>
                      <td data-label="Acciones">
                        <div className="acciones-buttons">
                          {evaluacion.completada ? (
                            <>
                              <button 
                                className="btn-accion btn-ver" 
                                onClick={() => verEvaluacion(evaluacion)}
                                title="Ver detalles"
                              >
                                👁️ Ver
                              </button>
                              <button 
                                className="btn-accion btn-descargar-pdf" 
                                onClick={() => verPDF(evaluacion)}
                                title="Descargar PDF"
                              >
                                📥 PDF
                              </button>
                              <button 
                                className="btn-accion btn-eliminar" 
                                onClick={() => eliminarEvaluacion(evaluacion.id)}
                                title="Eliminar evaluación"
                              >
                                🗑️ Eliminar
                              </button>
                            </>
                          ) : (
                            <>
                              <button 
                                className="btn-accion btn-continuar" 
                                onClick={() => editarEvaluacion(evaluacion.id)}
                                title="Continuar evaluación"
                              >
                                ▶️ Continuar
                              </button>
                              <button 
                                className="btn-accion btn-eliminar" 
                                onClick={() => eliminarEvaluacion(evaluacion.id)}
                                title="Eliminar evaluación"
                              >
                                🗑️ Eliminar
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>

      {mostrarDetalle && evaluacionSeleccionada && (
        <DetalleEvaluacionModal
          usuario={usuario.nombre}
          evaluacion={evaluacionSeleccionada}
          onCerrar={cerrarDetalle}
        />
      )}

      {mostrarResultadoPDF && evaluacionSeleccionada && (
        <ResultadoPopup
          usuario={usuario.nombre}
          puntuacion={evaluacionSeleccionada.puntuacion_total}
          nivelLiderazgo={getNivelLiderazgo(evaluacionSeleccionada.puntuacion_total)}
          onCerrar={cerrarResultadoPDF}
        />
      )}
    </>
  );
};

export default HistorialEvaluaciones;

